import { ReactNode } from "react";

/**
 * FAQ Item interface
 */
export interface FAQItem {
  /** Unique identifier (kebab-case) */
  id: string;
  /** Category (e.g., "Getting Started", "Pricing") */
  section: string;
  /** Question text */
  question: string;
  /** JSX content (can include links, images, videos) */
  answer: ReactNode;
  /** For flexible filtering */
  tags: string[];
  /** Optional: auto-show on specific pages */
  showOnPages?: string[];
  /** Optional: ordering within section (lower = higher priority) */
  priority?: number;
}

/**
 * FAQ Data
 * Add your FAQ items here
 */
export const faqData: FAQItem[] = [
  {
    id: "what-is-learntokiz",
    section: "Getting Started",
    question: "What is Learntokiz?",
    answer: (
      <>
        <p>
          Learntokiz is a comprehensive learning platform designed to help you master Urbankiz dancing. 
          We offer structured learning tracks, video tutorials, and a comprehensive dance dictionary 
          to support your learning journey.
        </p>
        <p>
          Whether you're a beginner or looking to refine your skills, Learntokiz provides the tools 
          and resources you need to progress at your own pace.
        </p>
      </>
    ),
    tags: ["overview", "basics", "getting-started"],
    showOnPages: ["landing", "home"],
    priority: 1,
  },
  {
    id: "how-to-get-started",
    section: "Getting Started",
    question: "How do I get started?",
    answer: (
      <>
        <p>
          Getting started with Learntokiz is easy:
        </p>
        <ul>
          <li>Create a free account to access basic content</li>
          <li>Browse our video library to explore available lessons</li>
          <li>Check out the Dance Dictionary to learn terminology</li>
          <li>Follow a learning track for structured progression</li>
        </ul>
        <p>
          Start with our beginner-friendly tracks and gradually work your way up to more advanced content.
        </p>
      </>
    ),
    tags: ["getting-started", "basics", "tutorial"],
    showOnPages: ["landing", "home"],
    priority: 2,
  },
  {
    id: "learning-tracks",
    section: "Features",
    question: "What are learning tracks?",
    answer: (
      <>
        <p>
          Learning tracks are curated sequences of videos designed to take you from beginner to 
          advanced levels in specific dance styles or techniques.
        </p>
        <p>
          Each track is carefully structured to build upon previous lessons, ensuring a logical 
          progression that helps you develop your skills systematically.
        </p>
      </>
    ),
    tags: ["features", "learning-tracks", "structure"],
    priority: 1,
  },
  {
    id: "dance-dictionary",
    section: "Features",
    question: "What is the Dance Dictionary?",
    answer: (
      <>
        <p>
          The Dance Dictionary is a comprehensive resource that explains dance terminology, 
          cultural context, and historical background for various dance styles.
        </p>
        <p>
          It includes entries for dance moves, cultural references, music genres, and historical 
          figures that are important to understanding Urbankiz and related dance forms.
        </p>
      </>
    ),
    tags: ["features", "dictionary", "terminology"],
    priority: 2,
  },
  {
    id: "account-requirements",
    section: "Account",
    question: "Do I need an account to use Learntokiz?",
    answer: (
      <>
        <p>
          While you can browse some content without an account, creating a free account gives you 
          access to:
        </p>
        <ul>
          <li>Full video library access</li>
          <li>Learning track progress tracking</li>
          <li>Personalized recommendations</li>
          <li>Bookmarking favorite videos</li>
        </ul>
      </>
    ),
    tags: ["account", "signup", "access"],
    priority: 1,
  },
  {
    id: "mobile-support",
    section: "Technical",
    question: "Can I use Learntokiz on mobile devices?",
    answer: (
      <>
        <p>
          Yes! Learntokiz is fully responsive and works great on mobile devices, tablets, and desktops. 
          You can access all features and content from any device with a modern web browser.
        </p>
      </>
    ),
    tags: ["technical", "mobile", "devices"],
    priority: 1,
  },
];

/**
 * Get FAQs filtered by section
 */
export const getFAQsBySection = (section: string): FAQItem[] => {
  return faqData
    .filter((faq) => faq.section === section)
    .sort((a, b) => (a.priority || 999) - (b.priority || 999));
};

/**
 * Get all unique FAQ sections
 */
export const getFAQSections = (): string[] => {
  return [...new Set(faqData.map((faq) => faq.section))];
};

